import { useState, useEffect } from "react";
import { Upload, FileText, CheckCircle, AlertCircle, Image, Download, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";

export default function AdminImport() {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [downloadingImages, setDownloadingImages] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const token = localStorage.getItem("session_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/import/status`, { headers });
      setStatus(response.data);
    } catch (error) {
      console.error("Error fetching status:", error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith(".xml")) {
        toast.error("Por favor selecciona un archivo XML");
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith(".xml")) {
        toast.error("Por favor selecciona un archivo XML");
        return;
      }
      setFile(droppedFile);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setResult(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/import/xml`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setResult(response.data);
      toast.success("Importación completada");
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error en la importación");
      setResult({ error: error.response?.data?.detail || "Error desconocido" });
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadImages = async () => {
    setDownloadingImages(true);
    let totalDownloaded = 0;
    let totalFailed = 0;

    try {
      // Download in batches
      for (let i = 0; i < 10; i++) {
        const response = await axios.post(`${API}/import/download-images?limit=50`, {}, { headers });
        totalDownloaded += response.data.downloaded;
        totalFailed += response.data.failed;
        
        if (response.data.processed === 0) break;
        
        fetchStatus();
        await new Promise(r => setTimeout(r, 1000)); // Wait between batches
      }
      
      toast.success(`Descargadas ${totalDownloaded} imágenes`);
      if (totalFailed > 0) {
        toast.warning(`${totalFailed} imágenes no se pudieron descargar`);
      }
    } catch (error) {
      toast.error("Error descargando imágenes");
    } finally {
      setDownloadingImages(false);
      fetchStatus();
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-import">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-text">
            Importar WordPress
          </h1>
          <p className="text-brand-textSecondary mt-1">
            Importa contenido desde un archivo XML de WordPress (WXR)
          </p>
        </div>

        {/* Status Card */}
        {status && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Estado de Importación</span>
                <Button variant="ghost" size="sm" onClick={fetchStatus}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-brand-bg rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-primary">{status.total_imported_posts}</p>
                  <p className="text-xs text-brand-textSecondary">Posts Importados</p>
                </div>
                <div className="bg-brand-bg rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{status.posts_with_images}</p>
                  <p className="text-xs text-brand-textSecondary">Con Imagen</p>
                </div>
                <div className="bg-brand-bg rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-amber-600">{status.posts_pending_images}</p>
                  <p className="text-xs text-brand-textSecondary">Sin Imagen</p>
                </div>
                <div className="bg-brand-bg rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-purple-600">{status.categories}</p>
                  <p className="text-xs text-brand-textSecondary">Categorías</p>
                </div>
                <div className="bg-brand-bg rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{status.tags}</p>
                  <p className="text-xs text-brand-textSecondary">Tags</p>
                </div>
              </div>

              {status.posts_pending_images > 0 && (
                <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Image className="h-5 w-5 text-amber-600" />
                      <div>
                        <p className="font-medium text-amber-800">
                          {status.posts_pending_images} posts necesitan descargar imagen
                        </p>
                        <p className="text-sm text-amber-600">
                          Descarga las imágenes desde WordPress
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleDownloadImages}
                      disabled={downloadingImages}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {downloadingImages ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Descargando...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Descargar Imágenes
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Upload Area */}
        <Card>
          <CardHeader>
            <CardTitle>Subir Archivo XML</CardTitle>
            <CardDescription>
              Exporta tu contenido desde WordPress y súbelo aquí
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors ${
                dragOver
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 hover:border-primary/50"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept=".xml"
                onChange={handleFileChange}
                className="hidden"
                id="xml-file"
                data-testid="xml-file-input"
              />
              <label htmlFor="xml-file" className="cursor-pointer">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                  <Upload className="h-8 w-8 text-primary" />
                </div>
                <p className="text-brand-text font-medium mb-2">
                  {file ? file.name : "Arrastra tu archivo XML aquí"}
                </p>
                <p className="text-sm text-brand-textSecondary">
                  o haz clic para seleccionar
                </p>
              </label>
            </div>

            {file && (
              <div className="mt-4 p-4 bg-brand-bg rounded-xl flex items-center gap-4">
                <FileText className="h-8 w-8 text-primary" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-brand-text truncate">{file.name}</p>
                  <p className="text-sm text-brand-textSecondary">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button
                  onClick={handleImport}
                  disabled={importing}
                  data-testid="import-btn"
                >
                  {importing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Importando...
                    </>
                  ) : (
                    "Importar"
                  )}
                </Button>
              </div>
            )}

            {importing && (
              <div className="mt-4">
                <Progress value={66} className="h-2" />
                <p className="text-sm text-brand-textSecondary mt-2 text-center">
                  Procesando archivo... esto puede tomar unos minutos
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        {result && !result.error && (
          <Card className="border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-800">
                <CheckCircle className="h-5 w-5" />
                Importación Completada
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-primary">
                    {result.imported?.posts || 0}
                  </p>
                  <p className="text-sm text-brand-textSecondary">Posts</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-purple-600">
                    {result.imported?.categories || 0}
                  </p>
                  <p className="text-sm text-brand-textSecondary">Categorías</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">
                    {result.imported?.tags || 0}
                  </p>
                  <p className="text-sm text-brand-textSecondary">Tags</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-gray-400">
                    {result.imported?.skipped || 0}
                  </p>
                  <p className="text-sm text-brand-textSecondary">Ya existían</p>
                </div>
              </div>
              
              {result.imported?.posts > 0 && (
                <div className="mt-4 p-4 bg-white rounded-xl">
                  <p className="text-sm text-green-700">
                    ✓ Posts importados correctamente. Ahora puedes descargar las imágenes usando el botón de arriba.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {result?.error && (
          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-800">
                <AlertCircle className="h-5 w-5" />
                Error en la importación
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-red-600">{result.error}</p>
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card>
          <CardHeader>
            <CardTitle>Cómo importar desde WordPress</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm text-brand-textSecondary">
            <ol className="list-decimal pl-4 space-y-3">
              <li>
                En tu WordPress, ve a <strong>Herramientas → Exportar</strong>
              </li>
              <li>
                Selecciona <strong>Todo el contenido</strong> o solo "Entradas"
              </li>
              <li>
                Haz clic en <strong>Descargar archivo de exportación</strong>
              </li>
              <li>
                Sube el archivo XML aquí y haz clic en <Badge variant="secondary">Importar</Badge>
              </li>
              <li>
                Una vez importado, haz clic en <Badge className="bg-amber-600">Descargar Imágenes</Badge> para traer las imágenes desde WordPress
              </li>
            </ol>
            
            <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-blue-800 text-sm">
                <strong>Nota:</strong> El proceso de descarga de imágenes puede tomar varios minutos dependiendo de la cantidad de posts. Las imágenes se descargarán en lotes de 50.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
